import { css } from '@emotion/react';
import {
  Button,
  Center,
  Flex,
  Pagination,
  SimpleGrid,
  Text,
  useResponsiveQuery,
} from '@inflearn/ds-react-next';
import { useLang } from '@inflearn/i18n-tools';
import { useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';

import type { SubscriptionCourseItem } from '../../../../queries/useSubscriptionCoursesQuery';
import { CourseCardLink } from '../../../common/CourseCardLink';
import CourseCardSkeleton from '../../../common/CourseCardSkeleton';
import EmptyStates from '../../../common/EmptyStates';

// 열 수는 B2C 강의 목록을 따른다 — smallMobile 2 / mobile 3 / tablet 이상 4
const GRID_COLS = { base: 2, xs: 3, sm: 4 };
const GRID_SPACING = { base: 12, xs: 16, sm: 20 };
const SKELETON_COUNT = 8;

interface SubscriptionCoursesGridProps {
  items: SubscriptionCourseItem[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  page: number;
  totalPage: number;
  hasFilter: boolean;
  onChangePage: (page: number) => void;
  onResetFilter: () => void;
  onClickLikeButton: (
    item: SubscriptionCourseItem,
  ) => (event: MouseEvent<HTMLButtonElement>) => void;
  onClickCartButton: (
    item: SubscriptionCourseItem,
  ) => (event: MouseEvent<HTMLButtonElement>) => void;
  onClickMoveToCartButton: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const SubscriptionCoursesGrid = ({
  items,
  totalCount,
  isLoading,
  isError,
  page,
  totalPage,
  hasFilter,
  onChangePage,
  onResetFilter,
  onClickLikeButton,
  onClickCartButton,
  onClickMoveToCartButton,
}: SubscriptionCoursesGridProps) => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const { isMobile, isSmallMobile } = useResponsiveQuery();

  if (isLoading) {
    return (
      <SimpleGrid cols={GRID_COLS} spacing={GRID_SPACING}>
        <CourseCardSkeleton count={SKELETON_COUNT} />
      </SimpleGrid>
    );
  }

  return (
    <EmptyStates
      isError={isError}
      isEmpty={items.length === 0}
      height={320}
      error={{
        title: t('subscription_courses.error.title'),
        body: t('subscription_courses.error.description'),
      }}
      empty={{
        title: t('subscription_courses.empty.title'),
        cta: hasFilter ? (
          <Button variant="default" onClick={onResetFilter}>
            {t('subscription_courses.empty.reset_filter')}
          </Button>
        ) : undefined,
      }}
    >
      <Flex direction="column" gap={24}>
        <Text size="sm" c="gray.6">
          {t('subscription_courses.result.total_count', {
            totalCount: totalCount.toLocaleString(),
          })}
        </Text>
        <SimpleGrid component="ul" css={styleCardGrid} cols={GRID_COLS} spacing={GRID_SPACING}>
          {items.map((item) => (
            <li key={item.id}>
              <CourseCardLink
                course={item}
                lang={lang}
                isHidePrice
                thumbLoading="lazy"
                onClickLikeButton={onClickLikeButton(item)}
                onClickCartButton={onClickCartButton(item)}
                onClickMoveToCartButton={onClickMoveToCartButton}
              />
            </li>
          ))}
        </SimpleGrid>
        <Center pt={16}>
          <Pagination
            value={page}
            total={Math.max(totalPage, 1)}
            onChange={onChangePage}
            radius="md"
            size={isMobile ? 'md' : 'lg'}
            boundaries={1}
            siblings={1}
            withControls={!isSmallMobile}
          />
        </Center>
      </Flex>
    </EmptyStates>
  );
};

const styleCardGrid = css({
  margin: 0,
  padding: 0,
  listStyle: 'none',
});
